import React, { useContext, useEffect, useState } from "react";
import { Box, Stack, Text, useMantineTheme } from "@mantine/core";
import { IconShieldCheck } from "@tabler/icons-react";
import { DataContext } from "../context/DataContext";
import { resolveOnAccentColor } from "../utils/onAccentColor";
import {
  normalizeColor,
  readColorParam,
  readStyleParam,
  isAnimationOff,
} from "../helpers/component-styles";

const readPrimaryColorFromUrl = () => {
  if (typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get("primaryColor");
  if (!raw) return null;
  return raw.startsWith("#") ? raw : `#${raw}`;
};

// Loading-screen translations live inline because this component renders
// BEFORE DataProvider/useLocale has any settings — most notably in root.js
// while the very first get_session is still in flight. Language is resolved
// from the URL (?language=) and then the browser, falling back to English.
const LOADING_MESSAGES = {
  en: "Getting your verification ready…",
  ar: "جاري إعداد التحقق من هويتك…",
  bg: "Подготвяме вашата проверка…",
  bs: "Pripremamo vašu verifikaciju…",
  cs: "Připravujeme vaše ověření…",
  da: "Forbereder din verificering…",
  de: "Verifizierung wird vorbereitet…",
  el: "Προετοιμασία της επαλήθευσής σας…",
  es: "Preparando tu verificación…",
  "es-419": "Preparando tu verificación…",
  et: "Valmistame teie kinnitamist ette…",
  fa: "در حال آماده‌سازی تأیید هویت شما…",
  fi: "Valmistellaan vahvistustasi…",
  fr: "Préparation de votre vérification…",
  he: "מכינים את האימות שלך…",
  hi: "आपका सत्यापन तैयार किया जा रहा है…",
  hr: "Pripremamo vašu verifikaciju…",
  hu: "Az ellenőrzés előkészítése…",
  hy: "Պատրաստում ենք ձեր ստուգումը…",
  id: "Menyiapkan verifikasi Anda…",
  it: "Preparazione della tua verifica…",
  ja: "確認の準備をしています…",
  ko: "인증을 준비하고 있습니다…",
  lt: "Ruošiamas jūsų patvirtinimas…",
  lv: "Sagatavojam jūsu verifikāciju…",
  mk: "Ја подготвуваме вашата верификација…",
  ms: "Menyediakan pengesahan anda…",
  nb: "Forbereder verifiseringen din…",
  nl: "Je verificatie wordt voorbereid…",
  pl: "Przygotowujemy twoją weryfikację…",
  pt: "Preparando sua verificação…",
  "pt-BR": "Preparando sua verificação…",
  ro: "Pregătim verificarea ta…",
  ru: "Подготавливаем вашу проверку…",
  sk: "Pripravujeme vaše overenie…",
  sq: "Po përgatisim verifikimin tuaj…",
  "sr-RS": "Припремамо вашу верификацију…",
  "sr-Latn-RS": "Pripremamo vašu verifikaciju…",
  sv: "Förbereder din verifiering…",
  th: "กำลังเตรียมการยืนยันของคุณ…",
  tl: "Inihahanda ang iyong pagberipika…",
  tr: "Doğrulamanız hazırlanıyor…",
  uk: "Готуємо вашу перевірку…",
  ur: "آپ کی تصدیق تیار کی جا رہی ہے…",
  vi: "Đang chuẩn bị xác minh của bạn…",
  zh: "正在准备您的验证…",
};

// Alternate message shown when this device is the mobile receiver of
// a desktop→mobile handoff. The handoff QR URL is built in
// pages/handoff/index.js with `?qr=true`, so we read that param to
// decide between "getting ready" (fresh entry) and "resuming your
// session" (handoff receiver, where the desktop already did the setup).
const HANDOFF_LOADING_MESSAGES = {
  en: "Resuming your session…",
  ar: "استئناف جلستك…",
  bg: "Възобновяване на сесията ви…",
  bs: "Nastavljanje vaše sesije…",
  cs: "Obnovování vaší relace…",
  da: "Genoptager din session…",
  de: "Sitzung wird fortgesetzt…",
  el: "Συνέχιση της συνεδρίας σας…",
  es: "Reanudando tu sesión…",
  "es-419": "Reanudando tu sesión…",
  et: "Teie seansi jätkamine…",
  fa: "در حال ادامه جلسه شما…",
  fi: "Istuntoasi jatketaan…",
  fr: "Reprise de votre session…",
  he: "ממשיכים את הסשן שלך…",
  hi: "आपका सत्र फिर से शुरू किया जा रहा है…",
  hr: "Nastavljanje vaše sesije…",
  hu: "Munkamenet folytatása…",
  hy: "Շարունակում ենք ձեր սեսսիան…",
  id: "Melanjutkan sesi Anda…",
  it: "Ripristino della tua sessione…",
  ja: "セッションを再開しています…",
  ko: "세션을 재개하는 중…",
  lt: "Atnaujinama jūsų sesija…",
  lv: "Atsākam jūsu sesiju…",
  mk: "Ја продолжуваме вашата сесија…",
  ms: "Menyambung semula sesi anda…",
  nb: "Gjenopptar økten din…",
  nl: "Sessie hervatten…",
  pl: "Wznawianie Twojej sesji…",
  pt: "A retomar a sua sessão…",
  "pt-BR": "Retomando sua sessão…",
  ro: "Reluăm sesiunea ta…",
  ru: "Возобновление вашей сессии…",
  sk: "Obnovovanie vašej relácie…",
  sq: "Po vazhdojmë sesionin tuaj…",
  "sr-RS": "Настављање ваше сесије…",
  "sr-Latn-RS": "Nastavljanje vaše sesije…",
  sv: "Återupptar din session…",
  th: "กำลังดำเนินการเซสชันต่อ…",
  tl: "Ipinagpapatuloy ang iyong session…",
  tr: "Oturumunuz devam ettiriliyor…",
  uk: "Відновлюємо ваш сеанс…",
  ur: "آپ کا سیشن دوبارہ شروع کیا جا رہا ہے…",
  vi: "Đang tiếp tục phiên của bạn…",
  zh: "正在恢复您的会话…",
};

const isHandoffReceiver = () => {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("qr") === "true";
};

// Ambiguous / partial language tags that publishers commonly send
// which don't map 1:1 to our concrete file keys. Kept in sync with
// the same alias map inside `hooks/useLocale.js`. Serbian has two
// scripts (Latin + Cyrillic); default plain `sr` to Latin because
// it's the more universally readable script on modern devices.
const LOCAL_ALIASES = {
  sr: "sr-Latn-RS",
  "sr-latn": "sr-Latn-RS",
  "sr-cyrl": "sr-RS",
};

const detectLanguage = (preferred) => {
  if (typeof window === "undefined") return "en";
  const urlLang = new URLSearchParams(window.location.search).get("language");
  const candidate = preferred || urlLang || navigator.language || "en";
  if (!candidate) return "en";
  // Try exact match (handles pt-BR, es-419, sr-Latn-RS)
  if (LOADING_MESSAGES[candidate]) return candidate;
  // Normalize case (e.g. en-US → en-us → en)
  const normalized = candidate.replace("_", "-");
  if (LOADING_MESSAGES[normalized]) return normalized;
  // Alias check — routes tags like `sr-Latn`, `sr-Cyrl`, plain
  // `sr` to the concrete file key we ship. Runs before the
  // case-insensitive fallback so the alias wins even when a
  // similar-but-different key exists.
  const lower = normalized.toLowerCase();
  if (LOCAL_ALIASES[lower] && LOADING_MESSAGES[LOCAL_ALIASES[lower]]) {
    return LOCAL_ALIASES[lower];
  }
  const caseInsensitive = Object.keys(LOADING_MESSAGES).find(
    (k) => k.toLowerCase() === lower,
  );
  if (caseInsensitive) return caseInsensitive;
  // Fall back to base language (en-US → en, es-MX → es). Same
  // alias check applied so `sr-Latn-XX` still routes to Latin.
  const base = normalized.split("-")[0];
  if (LOADING_MESSAGES[base]) return base;
  const baseLower = base.toLowerCase();
  if (LOCAL_ALIASES[baseLower] && LOADING_MESSAGES[LOCAL_ALIASES[baseLower]]) {
    return LOCAL_ALIASES[baseLower];
  }
  return "en";
};

const resolveDefaultMessage = () => {
  const lang = detectLanguage();
  const table = isHandoffReceiver() ? HANDOFF_LOADING_MESSAGES : LOADING_MESSAGES;
  return table[lang] || table.en;
};

// "Getting your verification ready…" in the session's language, for screens
// that mount after the session loaded and know `settings.language` (which
// may differ from the URL/browser language this component detects itself).
export const getLoadingMessage = (language) => {
  const lang = detectLanguage(language);
  return LOADING_MESSAGES[lang] || LOADING_MESSAGES.en;
};

/**
 * Full-screen initial loading state shown while the journey app is bootstrapping
 * (before get_session returns, before the route sequence is decided). Mirrors
 * the entrance animation of the post-verification passkey intro: a centered
 * circle scales in with a soft overshoot, then a single message fades in
 * just below it. The circle's background uses the publisher's primary color
 * — read from the explicit `primaryColor` prop, then the `?primaryColor=…`
 * URL param, then the Mantine theme — so the screen is on-brand from the
 * very first paint without waiting for the API response.
 */
const InitialLoadingScreen = ({
  primaryColor,
  message,
  // styles.components.loading.animation — "off" hides the dot-wave at
  // the bottom of the screen. Prop > session context > ?loadingAnimation=.
  animation,
}) => {
  const resolvedMessage = message || resolveDefaultMessage();
  const theme = useMantineTheme();
  // DataContext may not be mounted yet during root.js's first paint
  // (before the session loads). useContext on a missing provider
  // returns the default value passed to createContext (undefined for
  // this one) — so optional-chain everything.
  const dataContext = useContext(DataContext);
  // Resolve the accent in priority order:
  //   1. explicit `primaryColor` prop
  //   2. `?primaryColor=` URL param (publisher-branded session)
  //   3. Mantine's `teal[6]` (#12b886) — the same teal the rest of
  //      the unbranded app UI uses (phonematch icon, dcams modular,
  //      etc. all render `c="teal"` via ThemeIcon).
  //
  // We deliberately skip falling through to `theme.colors[theme.primaryColor]`
  // here — this component renders inside root.js's MantineProvider, which
  // does NOT register the publisher-brand "vx-green" palette (only the
  // inner DataProvider / App providers do), so a `theme.primaryColor`
  // lookup resolves to Mantine's default "blue" palette and the user
  // sees a generic blue when no URL param is provided.
  //
  // styles.components.loading.accentColor sits between the prop and
  // the primary URL param. Because this screen paints before
  // get_session resolves, dcams-create-session mirrors the value onto
  // the link as ?loadingAccentColor= (and ?loadingAnimation=), exactly
  // like ?primaryColor= today; the context read covers later mounts.
  const loadingStyles = dataContext?.data?.styles?.components?.loading;
  const accent =
    primaryColor ||
    normalizeColor(loadingStyles?.accentColor) ||
    readColorParam("loadingAccentColor") ||
    readPrimaryColorFromUrl() ||
    "#12b886";
  const animationSetting =
    animation ?? loadingStyles?.animation ?? readStyleParam("loadingAnimation");
  const showAnimation = !isAnimationOff(animationSetting);

  // The shield sits on the accent disc — the same visual role as a
  // filled Button's label — so it follows the publisher's button text
  // color (`styles.components.button.color`, the value App.js drives
  // filled buttons with). When that isn't set we derive the readable
  // foreground from the accent instead of defaulting to white, which
  // would disappear on a light primary.
  const buttonTextColor = resolveOnAccentColor({
    buttonTextColor: dataContext?.data?.styles?.components?.button?.color,
    accent,
  });

  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 30);
    return () => clearTimeout(t);
  }, []);

  const pageBg =
    theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white;

  return (
    <Box
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: pageBg,
        padding: 20,
        overflow: "hidden",
      }}
    >
      {/* Halftone dot wave — a dome of dotted texture rises from
          the bottom of the screen and breathes in/out. Two layers:
          the publisher's accent color and a hue-shifted complement
          so the wave reads as multi-color even when the brand is a
          single hue. The wave shape comes from a radial-gradient
          mask centered well below the screen so only the top
          curve of the gradient peeks up. */}
      {showAnimation && (
      <Box
        style={{
          position: "absolute",
          bottom: 0,
          insetInlineStart: 0,
          insetInlineEnd: 0,
          height: "55%",
          pointerEvents: "none",
          overflow: "hidden",
          zIndex: 0,
        }}
      >
        {/* Soft glow wash at the bottom — static color foundation
            that gives the dot dome its "lit from below" quality. */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(ellipse 90% 80% at 50% 115%, ${accent}55, ${accent}22 40%, transparent 75%)`,
            filter: "blur(40px)",
            opacity: 0.9,
          }}
        />

        {/* Base dot layer — static. Lower opacity so it reads as
            the "ambient" pattern, and the brighter highlight on
            top (next layer) can stand out against it. */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `radial-gradient(circle at center, ${accent} 1.5px, transparent 1.9px)`,
            backgroundSize: "11px 11px",
            WebkitMaskImage:
              "radial-gradient(ellipse 90% 85% at 50% 115%, #000 40%, transparent 78%)",
            maskImage:
              "radial-gradient(ellipse 90% 85% at 50% 115%, #000 40%, transparent 78%)",
            opacity: 0.55,
          }}
        />

        {/* Moving highlight — same dot grid as the base (so they
            don't moiré). Instead of a straight left-to-right sweep,
            the highlight wanders through the dense bottom region
            via a multi-point path that varies on both axes, so the
            motion reads as organic exploration rather than a
            mechanical slider. The path enters from off-screen left
            at opacity 0, wanders for the bulk of the cycle, and
            exits off-screen right at opacity 0 — invisible loop. */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `radial-gradient(circle at center, ${accent} 1.6px, transparent 2px)`,
            backgroundSize: "11px 11px",
            WebkitMaskImage:
              "radial-gradient(ellipse 30% 24% at 50% 78%, #000 25%, transparent 75%)",
            maskImage:
              "radial-gradient(ellipse 30% 24% at 50% 78%, #000 25%, transparent 75%)",
            animation:
              "initialLoadingSpotlight 11s ease-in-out infinite",
            // Negative delay = "fast-forward" the animation to its
            // midpoint so the highlight is visible in the middle of
            // the dome from the very first frame instead of fading
            // in from off-screen left.
            animationDelay: "-5.5s",
            "@keyframes initialLoadingSpotlight": {
              "0%":   { transform: "translate(-55%, 0%)",  opacity: 0 },
              "10%":  { transform: "translate(-38%, 4%)",  opacity: 0.85 },
              "22%":  { transform: "translate(-20%, -6%)", opacity: 1 },
              "35%":  { transform: "translate(10%, 5%)",   opacity: 1 },
              "48%":  { transform: "translate(-8%, -4%)",  opacity: 1 },
              "62%":  { transform: "translate(25%, 6%)",   opacity: 1 },
              "75%":  { transform: "translate(8%, -5%)",   opacity: 1 },
              "88%":  { transform: "translate(42%, 3%)",   opacity: 0.85 },
              "100%": { transform: "translate(60%, 0%)",   opacity: 0 },
            },
          }}
        />

        {/* Halo that travels with the spotlight on the same
            wandering path, painting a soft colored bloom under
            wherever the highlight currently is. */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(ellipse 24% 20% at 50% 80%, ${accent}88, transparent 65%)`,
            filter: "blur(36px)",
            animation:
              "initialLoadingSpotlightGlow 11s ease-in-out infinite",
            animationDelay: "-5.5s",
            "@keyframes initialLoadingSpotlightGlow": {
              "0%":   { transform: "translate(-55%, 0%)",  opacity: 0 },
              "10%":  { transform: "translate(-38%, 4%)",  opacity: 0.6 },
              "22%":  { transform: "translate(-20%, -6%)", opacity: 0.85 },
              "35%":  { transform: "translate(10%, 5%)",   opacity: 0.85 },
              "48%":  { transform: "translate(-8%, -4%)",  opacity: 0.9 },
              "62%":  { transform: "translate(25%, 6%)",   opacity: 0.85 },
              "75%":  { transform: "translate(8%, -5%)",   opacity: 0.85 },
              "88%":  { transform: "translate(42%, 3%)",   opacity: 0.6 },
              "100%": { transform: "translate(60%, 0%)",   opacity: 0 },
            },
          }}
        />
      </Box>
      )}

      <Stack align="center" spacing={24} style={{ position: "relative", zIndex: 1 }}>
        <Box
          style={{
            width: 88,
            height: 88,
            borderRadius: "50%",
            backgroundColor: accent,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 6px 22px ${accent}40`,
            transform: visible ? "scale(1)" : "scale(0.6)",
            opacity: visible ? 1 : 0,
            transition:
              "transform 600ms cubic-bezier(0.34, 1.56, 0.64, 1), opacity 400ms ease",
          }}
        >
          <Box
            sx={{
              animation: "initialLoadingPulse 1.8s ease-in-out infinite",
              animationDelay: "600ms",
              "@keyframes initialLoadingPulse": {
                "0%, 100%": { opacity: 1, transform: "scale(1)" },
                "50%": { opacity: 0.65, transform: "scale(0.92)" },
              },
              display: "flex",
            }}
          >
            <IconShieldCheck size={38} stroke={1.6} color={buttonTextColor} />
          </Box>
        </Box>
        <Text
          fw={600}
          fz={19}
          ta="center"
          c={theme.colorScheme === "dark" ? theme.colors.gray[4] : theme.colors.dark[8]}
          style={{
            whiteSpace: "nowrap",
            opacity: visible ? 1 : 0,
            transition: "opacity 500ms ease 200ms",
          }}
        >
          {resolvedMessage}
        </Text>
      </Stack>
    </Box>
  );
};

export default InitialLoadingScreen;
